import React, { memo, useCallback } from 'react';
import LinkIcon from '@mui/icons-material/Link';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { Card, IconButton, Typography } from '@mui/material';
import { ItemTypes } from "../../globals/types/DraggableItemTypes";
import { useDrag } from 'react-dnd';
import JobAppFieldsTypes from '../../globals/types/JobAppFieldsTypes';
import { setGlobalDragging } from '../../globals/dragEvents';

interface JobCardProps {
    isOwner: boolean | null | undefined;
    job: JobAppFieldsTypes;
    handleOpenJobApp: (job: JobAppFieldsTypes) => () => void;
    handleOpenJobLink: (job: JobAppFieldsTypes) => () => void;
    handleDeleteJobApp: (job: JobAppFieldsTypes) => () => void;
}

const JobCard = memo(function JobCard({
    isOwner,
    job,
    handleOpenJobApp,
    handleOpenJobLink,
    handleDeleteJobApp
}: JobCardProps) {
    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.CARD,
        item: () => {
            setGlobalDragging(true);
            return { ...job };
        },
        end: () => {
            setGlobalDragging(false);
        },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging()
        })
    }), [job]);

    const onOpenApp = useCallback(() => {
        if (typeof handleOpenJobApp === 'function') {
            const handler = handleOpenJobApp(job);
            if (typeof handler === 'function') {
                handler();
            }
        }
    }, [handleOpenJobApp, job]);

    const onOpenLink = useCallback(() => {
        if (typeof handleOpenJobLink === 'function') {
            const handler = handleOpenJobLink(job);
            if (typeof handler === 'function') {
                handler();
            }
        }
    }, [handleOpenJobLink, job]);

    const onDelete = useCallback(() => {
        if (typeof handleDeleteJobApp === 'function') {
            const handler = handleDeleteJobApp(job);
            if (typeof handler === 'function') {
                handler();
            }
        }
    }, [handleDeleteJobApp, job]);

    return (
        <Card 
            variant="outlined" 
            ref={isOwner ? drag : null}
            sx={{
                opacity: isDragging ? 0.4 : 1,
                cursor: isOwner ? (isDragging ? 'grabbing' : 'grab') : 'auto',
                color: 'white',
                position: 'relative',
                willChange: 'opacity, transform',
                transform: isDragging ? 'scale(0.98)' : 'scale(1)',
                transition: 'opacity 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                    boxShadow: isOwner ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
                }
            }}
        >
            <Typography variant="h5" color={"black"} align="left" margin={"1%"} marginLeft={"2%"}>
                <button className="link" type="button" onClick={onOpenApp}>
                    {job.company}
                </button>  
                {job.link && (
                    <IconButton onClick={onOpenLink} size="small" aria-label="open application link">
                        <LinkIcon />
                    </IconButton>
                )}
            </Typography>
            <Typography align="left" color="gray" margin={"1%"} marginLeft={"2%"}>
                {job.position}
            </Typography>
            {isOwner && (
                <IconButton 
                    style={{
                        position: 'absolute',
                        top: '0px',
                        right: '0px',
                    }} 
                    onClick={onDelete}
                    aria-label="delete job application"
                >
                    <DeleteRoundedIcon />
                </IconButton>
            )}
        </Card>
    );
});

export default JobCard;